import { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import produce from 'immer';

import { createLoadMoreMethod } from '../../../../../../util/apollo';
import { AlbumVideoSlideshow as AlbumVideoSlideshowQuery } from '../../../../../../graphql/queries/albums';
import { useContributorPermission } from '../../../../services/contributors';
import { albumVideoSlideshowUpdate as albumVideoSlideshowUpdateMutation } from '../../../../../../graphql/mutations/albums';
import { getErrorString } from '../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { VideoDelete as VideoDeleteMutation } from '../../../../../../graphql/mutations/video';

const useVideoSlideshow = ({ albumId }) => {
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { isOwner } = useContributorPermission({
    entityId: albumId,
  });

  const {
    fetchMore,
    loading,
    data: { album } = {},
  } = useQuery(AlbumVideoSlideshowQuery, {
    variables: { id: albumId },
    fetchPolicy: 'cache-and-network',
    skip: !albumId,
  });
  const loadMore = createLoadMoreMethod({
    source: album,
    recordsPath: 'slideshow.videos',
    fetchMore,
    limit: 10,
  });

  const [albumVideoSlideshowUpdate] = useMutation(
    albumVideoSlideshowUpdateMutation,
  );
  const [videoDelete] = useMutation(VideoDeleteMutation);

  const onLoadMore = async () => {
    if (loadingMore) {
      return;
    }

    setLoadingMore(true);

    try {
      await loadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  const onVideoDelete = async video => {
    if (saving) {
      return;
    }

    try {
      setSaving(true);
      await videoDelete({
        variables: {
          input: {
            video_id: video.id,
          },
        },
        update: store => {
          try {
            const data = store.readQuery({
              query: AlbumVideoSlideshowQuery,
              variables: { id: albumId },
            });

            const updatedData = produce(data, draft => {
              const { videos } = draft.album.slideshow;

              draft.album = {
                ...draft.album,
                slideshow: {
                  ...draft.album.slideshow,
                  videos: {
                    ...videos,
                    total: videos.total - 1,
                    records: videos.records.filter(
                      ({ video: slideshowVideo }) =>
                        slideshowVideo.id !== video.id,
                    ),
                  },
                },
              };
            });

            store.writeQuery({
              query: AlbumVideoSlideshowQuery,
              variables: { id: albumId },
              data: updatedData,
            });
          } catch {
            // Nothing to do here. Queries might not be loaded.
          }
        },
      });
    } catch (e) {
      openAlert({
        info: 'There was a problem deleting a video',
        message: getErrorString(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const onSlideshowUpdate = async ({
    status,
    mode,
    createVideos,
    createExcludedVideos,
    deleteExcludedVideos,
    videosLayout,
    queryOptions,
    albumInput,
  }) => {
    if (saving) {
      return;
    }

    setSaving(true);
    try {
      await albumVideoSlideshowUpdate({
        variables: {
          input: {
            id: albumId,
            slideshow: {
              status,
              mode,
              create_videos: createVideos,
              create_excluded_videos: createExcludedVideos,
              delete_excluded_videos: deleteExcludedVideos,
              videos_layout: videosLayout,
            },
            ...(albumInput ?? {}),
          },
        },
        update: (store, { data: { albumUpdate: response } }) => {
          const { slideshow: updatedSlideshow } = response.album;

          try {
            const data = store.readQuery({
              query: AlbumVideoSlideshowQuery,
              variables: { id: albumId },
            });

            const updatedData = produce(data, draft => {
              draft.album = {
                ...draft.album,
                slideshow: {
                  ...draft.album.slideshow,
                  ...updatedSlideshow,
                },
              };
            });

            store.writeQuery({
              query: AlbumVideoSlideshowQuery,
              variables: { id: albumId },
              data: updatedData,
            });
          } catch {
            // Nothing to do here. Queries might not be loaded.
          }
        },
        ...(queryOptions ?? {}),
      });
    } catch (err) {
      openAlert({
        message: 'There was an error updating the slideshow.',
        info: getErrorString(err),
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    album,
    saving,
    slideshow: album?.slideshow,
    videos: album?.slideshow?.videos ?? {},
    hasUnprocessedVideos:
      album && album?.videos?.total !== album?.processed_videos?.total,
    onSlideshowUpdate,
    loading,
    loadingMore,
    onLoadMore,
    onVideoDelete,
    isOwner,
  };
};

export default useVideoSlideshow;
